class OTPInputHandler {
  /**
   * Constructor for the OTPInputHandler class.
   * @param selector - The CSS selector for OTP input fields.
   */
  constructor(selector) {
    if (selector) {
      this.selector = selector;
      this.inputs = document.querySelectorAll(selector);
      if (this.inputs.length) {
        this.inputIndex = 0;
        this.alphaNumericPattern = /^[a-zA-Z0-9]$/;
        this.attachEventHandlers();
      } else {
        console.error('No elements found for the provided selector!');
      }
    }
  }

  /**
   * Attaches event listeners to elements matching the specified selector.
   */
  attachEvent(events, selector, callback) {
    const eventsList = events
      .split(',')
      .map((event) => event.trim())
      .filter((event) => ['keydown', 'paste', 'input'].includes(event));

    eventsList.forEach((event) => {
      document.addEventListener(event, (e) => {
        const target = e.target;
        if (target.closest(selector)) {
          callback.call(target, e);
        }
      });
    });
  }

  /**
   * Attaches event handlers for OTP input fields.
   */
  attachEventHandlers() {
    this.attachEvent('paste', this.selector, this.handlePaste);
    this.attachEvent('input', this.selector, this.handleInput);
  }

  /**
   * Handles keydown events for OTP input fields.
   */
  handleKeyDown = (e) => {
    const keyboard = e;

    if (keyboard.ctrlKey || keyboard.metaKey) {
      return;
    } else {
      e.preventDefault();
    }

    this.inputIndex = this.getInputIndex(e.target);
    switch (keyboard.key) {
      case 'Backspace':
        this.inputs[this.inputIndex].value = '';
        this.moveFocusLeft();
        break;
      case 'Delete':
        this.inputs[this.inputIndex].value = '';
        break;
      case 'ArrowLeft':
        this.moveFocusLeft();
        break;
      case 'ArrowRight':
        this.moveFocusRight();
        break;
      default:
        if (
          !this.allFilled() &&
          this.alphaNumericPattern.test(keyboard.key)
        ) {
          if (this.isEmptyInput(this.inputIndex)) {
            this.inputs[this.inputIndex].value = keyboard.key;
          }

          if (this.isEmptyInput(this.inputIndex + 1)) {
            this.moveFocusRight();
          }
        }
        break;
    }
  };

  /**
   * Handles paste events for OTP input fields.
   */
  handlePaste = (e) => {
    e.preventDefault();

    this.inputIndex = this.getInputIndex(e.target);
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) return;

    const pastedText = clipboardData.getData('text/plain').trim();
    this.distributeChars(pastedText);
  };

  /**
   * Handles input events (important for mobile keyboard paste & SMS autofill).
   */
  handleInput = (e) => {
    const value = e.target.value;
    this.inputIndex = this.getInputIndex(e.target);

    // Gestion du coller / autofill sur mobile (plusieurs caractères insérés d'un coup)
    if (value.length > 1) {
      this.distributeChars(value);
      return;
    }

    // Saisie unique normale
    if (this.alphaNumericPattern.test(value)) {
      this.moveFocusRight();
    }

    if (e.inputType === 'deleteContentBackward') {
      this.moveFocusLeft();
    }
  };

  /**
   * Répartit une chaîne de caractères sur les cases à partir de l'index courant.
   */
  distributeChars(text) {
    const chars = text
      .split('')
      .filter((char) => this.alphaNumericPattern.test(char))
      .slice(0, this.inputs.length - this.inputIndex);

    if (chars.length === 0) return;

    let lastIndex = this.inputIndex;
    for (let i = 0; i < chars.length; i++) {
      const targetIndex = this.inputIndex + i;
      this.setInputValue(targetIndex, chars[i]);
      lastIndex = targetIndex;
    }

    // Place le focus sur la case suivante ou la dernière case remplie
    const nextFocusIndex = Math.min(lastIndex + 1, this.inputs.length - 1);
    this.inputs[nextFocusIndex].focus();
  }

  updateInputs() {
    this.inputs = document.querySelectorAll(this.selector);
  }

  isEmptyInput(inputIndex) {
    return (
      inputIndex < this.inputs.length && this.inputs[inputIndex].value === ''
    );
  }

  setInputValue(inputIndex, value) {
    if (inputIndex >= 0 && inputIndex < this.inputs.length) {
      this.inputs[inputIndex].value = value;
    }
  }

  moveFocus = (direction) => {
    const nextIndex = this.inputIndex + direction;

    if (nextIndex >= 0 && nextIndex < this.inputs.length) {
      this.inputs[nextIndex].focus();
    }
  };

  moveFocusLeft = () => {
    this.moveFocus(-1);
  };

  moveFocusRight = () => {
    this.moveFocus(1);
  };

  allFilled() {
    return Array.from(this.inputs).every((input) => input.value !== '');
  }

  getInputIndex(input) {
    return Array.from(this.inputs).indexOf(input);
  }

  getOTP() {
    const otpValues = Array.from(this.inputs).map((input) => input.value);
    return otpValues.join('');
  }

  onInputEvent(callback) {
    if (!this.selector) {
      console.error('Selector is not defined!');
      return;
    }

    this.attachEvent('paste, input', this.selector, () => {
      callback(this.getOTP());
    });
  }
}

export { OTPInputHandler };